import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';
import '../styles/Auth.css';

interface Message {
  id: string;
  name: string;
  email: string;
  message: string;
  timestamp: string;
}

const Contact: React.FC = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ 
    name: user?.name || '', 
    email: user?.email || '', 
    message: '' 
  });
  const [recentChats, setRecentChats] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user && token) {
      fetch(`${API_URL}/api/contact`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(res => res.json())
        .then((data: Message[]) => {
          if (Array.isArray(data)) {
            setRecentChats(data.reverse()); // Show all recent
          }
        })
        .catch(err => {
          console.error('Error fetching recent chats:', err);
        });
    }
  }, [user, token]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    fetch(`${API_URL}/api/contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...formData,
        userId: user?.id
      }),
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          navigate('/messaging', { 
            state: { 
              chatId: data.id || Date.now().toString(),
              name: formData.name,
              email: formData.email
            } 
          });
        } else {
          setError('Failed to start chat. Please try again.');
        }
      })
      .catch(err => {
        console.error('Contact error:', err);
        setError('Connection error. Please check your internet.');
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="auth-container contact-page-wrapper" style={{ padding: '40px 20px', minHeight: 'calc(100vh - 80px)' }}>
      <div style={{ 
        width: '100%', 
        maxWidth: '1200px', 
        display: 'flex', 
        flexDirection: window.innerWidth < 900 ? 'column' : 'row', 
        gap: '40px',
        alignItems: 'flex-start'
      }}>
        
        {/* LEFT SIDE: RECENT CHATS */}
        {user && recentChats.length > 0 && (
          <div className="auth-card recent-chats-sidebar" style={{ 
            flex: '0 0 350px', 
            padding: '25px', 
            backgroundColor: '#f8f9fa', 
            border: '1px solid #eee',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ fontSize: '1.1rem', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px', color: '#333' }}>
              💬 Recent Conversations
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {recentChats.map(chat => (
                <div 
                  key={chat.id} 
                  onClick={() => navigate('/messaging', { state: { chatId: chat.id, name: chat.name, email: chat.email } })}
                  style={{ 
                    padding: '15px', 
                    backgroundColor: 'white', 
                    borderRadius: '12px', 
                    cursor: 'pointer',
                    border: '1px solid #ddd',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.02)'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = 'var(--primary-color)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = '#ddd';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <strong style={{ display: 'block', fontSize: '0.85rem', color: '#333', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    "{chat.message}"
                  </strong>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <small style={{ color: '#999', fontSize: '0.7rem' }}>{new Date(chat.timestamp).toLocaleDateString()}</small>
                    <span style={{ color: 'var(--primary-color)', fontWeight: 'bold' }}>→</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* RIGHT SIDE: CONTACT FORM */}
        <div className="auth-card" style={{ flex: 1, padding: '40px', textAlign: 'left' }}>
          <h1 style={{ textAlign: 'left', marginBottom: '10px' }}>Contact Support</h1>
          <p style={{ textAlign: 'left', marginBottom: '30px' }}>Start a new live chat with our team and AI assistant.</p>
          
          <form onSubmit={handleSubmit} className="auth-form">
            {error && <div className="auth-error">{error}</div>}
            
            <div className="form-group">
              <label>Your Name</label>
              <input 
                type="text" 
                placeholder="John Doe" 
                value={formData.name}
                onChange={e => setFormData({...formData, name: e.target.value})}
                required 
              />
            </div>

            <div className="form-group">
              <label>Your Email</label>
              <input 
                type="email" 
                placeholder="john@example.com" 
                value={formData.email}
                onChange={e => setFormData({...formData, email: e.target.value})}
                required 
              />
            </div>

            <div className="form-group">
              <label>What is your question?</label>
              <textarea 
                placeholder="Type here to begin your chat..." 
                value={formData.message}
                onChange={e => setFormData({...formData, message: e.target.value})}
                required 
                style={{ 
                  width: '100%', 
                  padding: '15px', 
                  borderRadius: '12px', 
                  border: '1px solid #ddd',
                  minHeight: '120px',
                  fontSize: '1rem',
                  fontFamily: 'inherit'
                }}
              ></textarea>
            </div>
            <button type="submit" className="auth-btn" disabled={loading} style={{ width: '100%' }}>
              {loading ? 'Connecting...' : 'Start New Chat'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Contact;
